import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setFollowUpCare as updateFollowUpCare } from "../../redux/userSlice";
import Question from "../../components/Question";
import { Box, FormControl, FormControlLabel, Checkbox, useTheme } from "@mui/material";
import ContinueButton from "../../components/ContinueButton";
import { checkboxSx, continueButtonSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function FollowUpCare() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const options = useMemo(
        () => ({
            group1: {
                "I like my treatment, I want to refill my prescription":
                    "I like my treatment, I want to refill my prescription",
                "I need a different treatment or adjustment to my current one":
                    "I need a different treatment or adjustment to my current one",
            },
            group2: {
                "I have questions for the provider": "I have questions for the provider",
                "I have another reason not listed above":
                    "I have another reason not listed above",
            },
        }),
        []
    );

    const user = useSelector((state) => state.user);
    const [followUpCare, setFollowUpCare] = useState([]);
    const { sideEffects } = useSelector((state) => state.user);

    useEffect(() => {
        const followUpArray = Array.isArray(user.followUpCare)
            ? user.followUpCare
            : typeof user.followUpCare === "string"
                ? [user.followUpCare]
                : [];
        setFollowUpCare(followUpArray);
    }, [user.followUpCare]);

    const [whyStoppedMedication, setWhyStoppedMedication] = useState([]);

    useEffect(() => {
        const stoppedArray = Array.isArray(user.whyStoppedMedication)
            ? user.whyStoppedMedication
            : typeof user.whyStoppedMedication === "string"
                ? [user.whyStoppedMedication]
                : [];
        setWhyStoppedMedication(stoppedArray);
    }, [user.whyStoppedMedication]);

    const handleChange = useCallback(
        (e) => {
            const { name, checked } = e.target;
            const group = Object.keys(options).find((groupKey) =>
                Object.keys(options[groupKey]).includes(name)
            );

            setFollowUpCare((prev) => {
                let newFollowUpCare = [...prev];

                if (checked) {
                    newFollowUpCare = newFollowUpCare.filter(item => !Object.keys(options[group]).includes(item));
                    newFollowUpCare.push(name);
                } else {
                    newFollowUpCare = newFollowUpCare.filter(item => item !== name);
                }

                return newFollowUpCare;
            });
        },
        [options]
    );

    const handleNext = useCallback(() => {
        dispatch(updateFollowUpCare(followUpCare));
        if (
            followUpCare.includes("I have questions for the provider") ||
            followUpCare.includes("I have another reason not listed above")
        ) {
            setPageAndDeferNavigate(dispatch, '/questions-for-doctor', navigate);
        } else {
            if (
                user.sex?.sexAtBirth === "Female" ||
                user.sex?.sexAtBirth === "Prefer not to answer"
            ) {
                setPageAndDeferNavigate(dispatch, '/maternity', () =>
                    navigate('/maternity', { state: { returning: true } })
                );
            } else {
                setPageAndDeferNavigate(dispatch, '/recent-photo', () =>
                    navigate('/recent-photo', { state: { returning: true } })
                );
            }
        }
    }, [followUpCare, dispatch, navigate, user.sex]);

    const isNextEnabled = followUpCare.length > 0;

    const handleBack = () => {
        if (user.onPrescribedMedication === "no" && whyStoppedMedication.includes("I had some side effects")) {
            setPageAndDeferNavigate(dispatch, '/describe-side-effects', navigate);
        } else if (user.onPrescribedMedication === "no") {
            setPageAndDeferNavigate(dispatch, '/stop-medication-reason', navigate);
        } else {
            setPageAndDeferNavigate(dispatch, '/working-well', navigate);
        }
    };


    return (
        <Question
            onBack={handleBack}
            title="Got it. And what are you hoping to get out of this follow-up care?"
            subtitle="You can select multiple responses"
            input={
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "400px",
                            [theme.breakpoints.up("sm")]: {
                                margin: "0 auto",
                            },
                        }}
                    >
                        <FormControl
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                                mb: "2rem",
                            }}
                        >
                            {Object.entries(options)
                                .flatMap(([groupName, groupOptions]) =>
                                    Object.entries(groupOptions)
                                )
                                .map(([key, value]) => (
                                    <FormControlLabel
                                        key={key}
                                        control={
                                            <Checkbox
                                                checked={followUpCare.includes(key)}
                                                onChange={handleChange}
                                                name={key}
                                            />
                                        }
                                        label={value}
                                        sx={{ ...checkboxSx }}
                                    />
                                ))}
                        </FormControl>
                    </Box>
                </Box>
            }
            footer={
                <Box
                    sx={{ ...continueButtonSx }}
                >
                    <ContinueButton
                        onClick={handleNext}
                        title="Continue"
                        selected={isNextEnabled}
                        disabled={!isNextEnabled}
                    />
                </Box>
            }
        />
    );
}
